import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import Select, { components, OptionProps } from 'react-select';

import { Dispatch } from 'redux';
import { UPDATE_SELECTED_CHILD_COURSE_ID, UPDATE_SELECTED_PARENT_COURSE_ID } from 'redux/actions';
import { RootState } from 'redux/reducers';
import { ROUTES } from 'routes';

import css from './ChildCourseSelector.module.scss';

interface SelectOption {
  label: string;
  value: string;
  isImported: NumericBoolean;
}

const SelectCustomOption = ({ children, data, ...props }: OptionProps<SelectOption, false>) => {
  return (
    <components.Option {...props} data={data}>
      <div className={css['child-course-selector-custom-option']}>
        <div className={css['child-course-selector-custom-option-label']}>{children}</div>
        {data.isImported ? (
          <div className={css['child-course-selector-custom-option-status']}>Imported</div>
        ) : null}
      </div>
    </components.Option>
  );
};

export interface ChildCourseSelectorProps extends RouteComponentProps {
  sessionData: SessionData | null;
  selectedParentCourseId: string | null;
  selectedChildCourseId: string | null;
  updateSelectedParentCourseId: (courseId: string | undefined) => void;
  updateSelectedChildCourseId: (childCourseId: string | undefined) => void;
}

const ChildCourseSelector = ({
  sessionData,
  selectedParentCourseId,
  selectedChildCourseId,
  updateSelectedParentCourseId,
  updateSelectedChildCourseId,
  history,
}: ChildCourseSelectorProps) => {
  if (!sessionData) return null;
  if (!selectedParentCourseId) return null;

  if (sessionData.courseDetails.hasChildren === 0) return null;

  const parent = [
    {
      label: `${sessionData.courseDetails.code}: ${sessionData.courseDetails.title}`,
      value: sessionData.courseDetails.courseId,
      isImported: sessionData.courseDetails.isImported,
    },
  ];

  const children = sessionData.courseDetails.childCourses.map((childCourse) => ({
    label: `${childCourse.code}: ${childCourse.title}`,
    value: childCourse.childId,
    isImported: childCourse.isImported,
  }));

  const options = [
    {
      label: 'Parent course',
      options: parent,
    },
    {
      label: 'Children courses',
      options: children,
    },
  ];

  let value;
  if (selectedChildCourseId == null) {
    value = parent[0];
  } else {
    value = children.find((option) => option.value === selectedChildCourseId);
  }

  const handleChangeChildCourse = (selectedOption: { label: string; value: string } | null) => {
    const selectedId = selectedOption?.value;
    if (!selectedId) return;

    if (selectedId === sessionData.courseDetails.courseId) {
      updateSelectedParentCourseId(selectedId);
      updateSelectedChildCourseId(undefined);
      history.push(sessionData.courseDetails.isImported ? ROUTES.COURSES : ROUTES.COURSES_CREATE);
      return;
    }

    const childCourse = sessionData.courseDetails.childCourses.find(
      (childCourse) => childCourse.childId === selectedId
    );
    if (!childCourse) return;

    updateSelectedChildCourseId(selectedId);
    history.push(childCourse.isImported ? ROUTES.COURSES : ROUTES.COURSES_CREATE);
  };

  return (
    <>
      <div className={css['divider']} />
      <Select
        id='child-course-selector'
        className={css['child-course-selector']}
        options={options}
        value={value}
        onChange={handleChangeChildCourse}
        components={{ Option: SelectCustomOption }}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    sessionData: state.getAccess,
    selectedParentCourseId: state.selectedParentCourseId,
    selectedChildCourseId: state.selectedChildCourseId,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateSelectedParentCourseId: (courseId: string | undefined) => {
    dispatch({
      type: UPDATE_SELECTED_PARENT_COURSE_ID,
      payload: courseId,
    });
  },
  updateSelectedChildCourseId: (childCourseId: string | undefined) => {
    dispatch({
      type: UPDATE_SELECTED_CHILD_COURSE_ID,
      payload: childCourseId,
    });
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ChildCourseSelector));
